import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Trash2, Check, ChevronDown, ChevronRight, Flag } from "lucide-react";

const quadrantLabels = {
  "hi-impact-lo-effort": "Quick Win",
  "hi-impact-med-effort": "High-Value",
  "hi-impact-hi-effort": "Big Project",
  "med-impact-lo-effort": "Easy Gain",
  "med-impact-med-effort": "Steady Work",
  "med-impact-hi-effort": "Consider",
  "lo-impact-lo-effort": "Fill-In",
  "lo-impact-med-effort": "Low Priority",
  "lo-impact-hi-effort": "Avoid",
};

function SubtaskProgress({ task }) {
  const subtasks = task.subtasks || [];
  if (subtasks.length === 0) return null;
  const done = subtasks.filter((s) => s.completed).length;
  const total = subtasks.length;
  const pct = (done / total) * 100;
  return (
    <div className="priority-subtask-info">
      <div className="priority-subtask-bar">
        <div className="priority-subtask-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="priority-subtask-count">{done}/{total}</span>
    </div>
  );
}

function InlineSubtasks({ task, onToggleSubtask }) {
  const subtasks = task.subtasks || [];
  if (subtasks.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="inline-subtasks-wrapper"
    >
      <div className="inline-subtasks" data-testid="inline-subtask-list">
        {subtasks.map((st) => (
          <div key={st.id} className="inline-subtask-item">
            <button
              className={`inline-subtask-check ${st.completed ? "checked" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSubtask(task.id, st.id);
              }}
              data-testid="inline-subtask-toggle"
            >
              {st.completed && <Check size={9} />}
            </button>
            <span className={`inline-subtask-name ${st.completed ? "completed" : ""}`}>
              {st.name}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function PrioritizedList({ tasks, completedTasks, onToggle, onEdit, onDelete, onToggleSubtask, energyLevel }) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (taskId, task) => {
    const subtasks = task.subtasks || [];
    if (subtasks.length === 0) return;
    setExpandedId((prev) => (prev === taskId ? null : taskId));
  };

  if (tasks.length === 0 && completedTasks.length === 0) {
    return (
      <div className="list-empty" data-testid="prioritized-todo-list">
        <div className="list-empty-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect x="4" y="8" width="32" height="24" rx="4" stroke="#7A9E7E" strokeWidth="1.5" opacity="0.3" />
            <line x1="12" y1="16" x2="28" y2="16" stroke="#C9A84C" strokeWidth="1.5" opacity="0.2" />
            <line x1="12" y1="22" x2="24" y2="22" stroke="#C9A84C" strokeWidth="1.5" opacity="0.15" />
            <line x1="12" y1="28" x2="20" y2="28" stroke="#C9A84C" strokeWidth="1.5" opacity="0.1" />
          </svg>
        </div>
        <div className="list-empty-text">No tasks prioritized yet</div>
        <div className="list-empty-hint">Drag tasks into the matrix to see them here</div>
      </div>
    );
  }

  return (
    <div data-testid="prioritized-todo-list">
      <div className="priority-list">
        <AnimatePresence mode="popLayout">
          {tasks.map((task, index) => {
            const hasSubtasks = (task.subtasks || []).length > 0;
            const isExpanded = expandedId === task.id;
            const isUrgent = task.urgent || false;

            return (
              <motion.div
                key={task.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
              >
                <div className={`priority-item-block ${isExpanded ? "expanded" : ""} ${isUrgent ? "urgent" : ""}`}>
                  <div
                    className={`priority-item ${hasSubtasks ? "has-subtasks" : ""}`}
                    onClick={() => toggleExpand(task.id, task)}
                  >
                    <button
                      className="priority-checkbox"
                      onClick={(e) => { e.stopPropagation(); onToggle(task.id); }}
                      data-testid="task-complete-checkbox"
                    >
                      {task.completed && (
                        <span className="priority-checkbox-inner">
                          <Check size={10} />
                        </span>
                      )}
                    </button>
                    <div className="priority-item-content">
                      <div className="priority-item-name-row">
                        {hasSubtasks && (
                          <span className="expand-chevron">
                            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </span>
                        )}
                        <span className={`priority-item-name ${task.completed ? "completed" : ""}`}>
                          {task.name}
                        </span>
                        {isUrgent && (
                          <span className="priority-urgent-badge" data-testid="priority-urgent-badge">
                            <Flag size={10} />
                            Urgent
                          </span>
                        )}
                      </div>
                      <div className="priority-item-meta">
                        <span className={`meta-badge meta-badge-${task.category}`}>
                          {task.category}
                        </span>
                        {task.quadrant && (
                          <span className="meta-badge meta-badge-quadrant">
                            {quadrantLabels[task.quadrant] || task.quadrant}
                          </span>
                        )}
                      </div>
                      {!isExpanded && <SubtaskProgress task={task} />}
                    </div>
                    <div className="priority-item-actions">
                      <button
                        className="list-action-btn"
                        onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                        title="Edit"
                        data-testid="task-edit-button"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="list-action-btn delete-btn"
                        onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
                        title="Delete"
                        data-testid="task-delete-button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Inline subtasks - expanded */}
                  <AnimatePresence>
                    {isExpanded && (
                      <InlineSubtasks
                        task={task}
                        onToggleSubtask={onToggleSubtask}
                      />
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Completed Section */}
      {completedTasks.length > 0 && (
        <div className="completed-section">
          <button
            className="completed-toggle"
            onClick={() => setShowCompleted(!showCompleted)}
          >
            {showCompleted ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Completed
            <span className="completed-count">{completedTasks.length}</span>
          </button>
          {showCompleted && (
            <div className="priority-list">
              <AnimatePresence>
                {completedTasks.map((task) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="priority-item">
                      <button
                        className="priority-checkbox checked"
                        onClick={() => onToggle(task.id)}
                        data-testid="task-complete-checkbox"
                      >
                        <span className="priority-checkbox-inner">
                          <Check size={10} />
                        </span>
                      </button>
                      <div className="priority-item-content">
                        <div className="priority-item-name completed">
                          {task.name}
                        </div>
                        <div className="priority-item-meta">
                          <span className={`meta-badge meta-badge-${task.category}`}>
                            {task.category}
                          </span>
                        </div>
                      </div>
                      <div className="priority-item-actions">
                        <button
                          className="list-action-btn delete-btn"
                          onClick={() => onDelete(task.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
